import React, { useState, useEffect } from 'react'
import AppBar from '../components/AppBar'
import ToggleSwitch from '../components/ToggleSwitch'
import LoadingSpinner from '../components/LoadingSpinner'
import Gap from '../components/Gap'
import LegalDocViewer, { LegalDocType } from '../components/LegalDocViewer'
import { userApi } from '../api'
import { toastUtil } from '../utils/toast'

const ConsentManagement: React.FC = () => {
  // 약관/방침을 헤더+뒤로가기 있는 인앱 뷰어로 표시(공개 라우트로 이동하면 앱 셸이 없어 웹처럼 보임)
  const [legalViewer, setLegalViewer] = useState<LegalDocType | null>(null)
  const [locationConsent, setLocationConsent] = useState(false)
  const [locationConsentAt, setLocationConsentAt] = useState<string | null>(
    null
  )
  const [isLocationConsentUpdating, setIsLocationConsentUpdating] =
    useState(false)
  const [contactConsent, setContactConsent] = useState(false)
  const [contactConsentAt, setContactConsentAt] = useState<string | null>(null)
  const [isContactConsentUpdating, setIsContactConsentUpdating] =
    useState(false)
  const [pushNotificationConsent, setPushNotificationConsent] = useState(false)
  const [pushNotificationConsentAt, setPushNotificationConsentAt] = useState<
    string | null
  >(null)
  const [
    isPushNotificationConsentUpdating,
    setIsPushNotificationConsentUpdating,
  ] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // 날짜 포맷 함수 (YYYY-MM-DD -> YYYY년 M월 D일)
  const formatConsentDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${year}년 ${month}월 ${day}일`
  }

  // 설정 초기값 로드
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true)
      try {
        const response = await userApi.getProfile()
        if (response.user.location_consent !== undefined) {
          setLocationConsent(response.user.location_consent)
        }
        if (response.user.location_consent_at) {
          setLocationConsentAt(response.user.location_consent_at)
        }
        if (response.user.contact_consent !== undefined) {
          setContactConsent(response.user.contact_consent)
        }
        if (response.user.contact_consent_at) {
          setContactConsentAt(response.user.contact_consent_at)
        }
        if (response.user.push_notification_consent !== undefined) {
          setPushNotificationConsent(response.user.push_notification_consent)
        }
        if (response.user.push_notification_consent_at) {
          setPushNotificationConsentAt(
            response.user.push_notification_consent_at
          )
        }
      } catch (error: any) {
        // 설정 로드 실패 시 기본값 유지
      } finally {
        setIsLoading(false)
      }
    }

    loadSettings()
  }, [])

  // 위치 정보 이용 동의 변경 핸들러
  const handleLocationConsentChange = async (value: boolean) => {
    if (isLocationConsentUpdating) return

    setIsLocationConsentUpdating(true)
    const previousConsent = locationConsent
    const previousConsentAt = locationConsentAt
    setLocationConsent(value)

    try {
      await userApi.updateLocationConsent(value)
      // 업데이트 후 프로필을 다시 조회하여 서버에서 설정한 날짜 가져오기
      const response = await userApi.getProfile()
      if (response.user.location_consent !== undefined) {
        setLocationConsent(response.user.location_consent)
      }
      if (response.user.location_consent_at) {
        setLocationConsentAt(response.user.location_consent_at)
      } else {
        setLocationConsentAt(null)
      }
    } catch (error: any) {
      // 실패 시 이전 값으로 되돌림
      setLocationConsent(previousConsent)
      setLocationConsentAt(previousConsentAt)
      console.error('위치 정보 이용 동의 변경 실패:', error)
    } finally {
      setIsLocationConsentUpdating(false)
    }
  }

  // 연락처 연동 동의 변경 핸들러
  const handleContactConsentChange = async (value: boolean) => {
    if (isContactConsentUpdating) return

    setIsContactConsentUpdating(true)
    const previousConsent = contactConsent
    const previousConsentAt = contactConsentAt
    setContactConsent(value)

    try {
      await userApi.updateContactConsent(value)
      // 업데이트 후 프로필을 다시 조회하여 서버에서 설정한 날짜 가져오기
      const response = await userApi.getProfile()
      if (response.user.contact_consent !== undefined) {
        setContactConsent(response.user.contact_consent)
      }
      if (response.user.contact_consent_at) {
        setContactConsentAt(response.user.contact_consent_at)
      } else {
        setContactConsentAt(null)
      }
    } catch (error: any) {
      // 실패 시 이전 값으로 되돌림
      setContactConsent(previousConsent)
      setContactConsentAt(previousConsentAt)
      toastUtil.error('연락처 연동 동의 변경에 실패했습니다')
    } finally {
      setIsContactConsentUpdating(false)
    }
  }

  // 푸시 알림 동의 변경 핸들러
  const handlePushNotificationConsentChange = async (value: boolean) => {
    if (isPushNotificationConsentUpdating) return

    setIsPushNotificationConsentUpdating(true)
    const previousConsent = pushNotificationConsent
    const previousConsentAt = pushNotificationConsentAt
    setPushNotificationConsent(value)

    try {
      await userApi.updatePushNotificationConsent(value)
      // 업데이트 후 프로필을 다시 조회하여 서버에서 설정한 날짜 가져오기
      const response = await userApi.getProfile()
      if (response.user.push_notification_consent !== undefined) {
        setPushNotificationConsent(response.user.push_notification_consent)
      }
      if (response.user.push_notification_consent_at) {
        setPushNotificationConsentAt(response.user.push_notification_consent_at)
      } else {
        setPushNotificationConsentAt(null)
      }
    } catch (error: any) {
      // 실패 시 이전 값으로 되돌림
      setPushNotificationConsent(previousConsent)
      setPushNotificationConsentAt(previousConsentAt)
      toastUtil.error('광고성 푸시 알림 동의 변경에 실패했습니다')
    } finally {
      setIsPushNotificationConsentUpdating(false)
    }
  }

  // 동의 항목 렌더링 함수
  const _buildConsentItem = (
    title: string,
    checked: boolean,
    consentAt: string | null,
    onChange: (value: boolean) => void
  ) => {
    const handleToggleClick = (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(!checked)
    }

    return (
      <div
        className="w-full px-20px py-16px flex items-center justify-between cursor-pointer"
        onClick={() => onChange(!checked)}
      >
        <div className="flex-1 flex flex-col">
          <span className="font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
            {title}
          </span>
          {checked && consentAt && (
            <span className="font-pretendard font-regular text-14px leading-140% text-textColorText3 mt-4px">
              동의일자 {formatConsentDate(consentAt)}
            </span>
          )}
        </div>
        <Gap width={8} />
        <div onClick={handleToggleClick}>
          <ToggleSwitch checked={checked} onChange={onChange} />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar title="이용약관" isBottomBorder />

      <div className="app-container flex-1 overflow-y-auto scrollbar-hide">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-20px">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <Gap height={8} />

            {/* 개인정보처리방침 */}
            <div
              className="w-full px-20px py-16px flex items-center justify-between cursor-pointer"
              onClick={() => setLegalViewer('privacy')}
            >
              <span className="flex-1 font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
                개인정보처리방침
              </span>
              <Gap width={8} />
              <img
                src="/images/bold-arrow-right-icon.png"
                alt="이동"
                className="w-24px h-24px object-contain"
              />
            </div>

            {/* 이용약관 */}
            <div
              className="w-full px-20px py-16px flex items-center justify-between cursor-pointer"
              onClick={() => setLegalViewer('terms')}
            >
              <span className="flex-1 font-pretendard font-bold text-16px leading-180% text-brown-80%-main">
                이용약관
              </span>
              <Gap width={8} />
              <img
                src="/images/bold-arrow-right-icon.png"
                alt="이동"
                className="w-24px h-24px object-contain"
              />
            </div>

            {/* 위치 정보 이용 동의 */}
            {_buildConsentItem(
              '위치 정보 이용 동의',
              locationConsent,
              locationConsentAt,
              handleLocationConsentChange
            )}

            {/* 연락처 연동 동의 */}
            {_buildConsentItem(
              '연락처 연동 동의',
              contactConsent,
              contactConsentAt,
              handleContactConsentChange
            )}

            {/* 광고성 푸시 알림 동의 */}
            {_buildConsentItem(
              '광고성 푸시 알림 동의',
              pushNotificationConsent,
              pushNotificationConsentAt,
              handlePushNotificationConsentChange
            )}
          </>
        )}
      </div>

      {/* 약관/방침 인앱 뷰어(헤더+뒤로가기) */}
      <LegalDocViewer type={legalViewer} onClose={() => setLegalViewer(null)} />
    </div>
  )
}

export default ConsentManagement
